import { FlowTimeUnits } from '../sensor/SensorBase';
import { DisplayUnit } from './DisplayUnit';
import { MeasurementUnit } from './MeasurementUnit';

const flowTimeSuffixes: Record<FlowTimeUnits, string> = {
  NANOS: '/ns',
  MICROS: '/µs',
  MILLIS: '/ms',
  SECONDS: '/s',
  MINUTES: '/min',
  HOURS: '/h',
  HALFDAYS: '/½d',
  DAYS: '/d',
  WEEKS: '/w',
  MONTHS: '/mo',
  YEARS: '/y',
  DECADES: '/dec',
  CENTURIES: '/c',
  MILLENNIA: '/ky',
  ERAS: '/era',
  FOREVER: '',
};

const flowForUnit = (unit: MeasurementUnit): Record<FlowTimeUnits, string> =>
  (Object.keys(flowTimeSuffixes) as FlowTimeUnits[]).reduce(
    (acc: Record<FlowTimeUnits, string>, key: FlowTimeUnits) => {
      acc[key] = unit ? `${DisplayUnit[unit]}${flowTimeSuffixes[key]}` : '';
      return acc;
    },
    {} as Record<FlowTimeUnits, string>
  );

export const displayFlowUnit: Record<MeasurementUnit, Record<FlowTimeUnits, string>> = {
  [MeasurementUnit.PULSES]: flowForUnit(MeasurementUnit.PULSES),
  [MeasurementUnit.AMPER]: flowForUnit(MeasurementUnit.AMPER),
  [MeasurementUnit.MILLIAMPER]: flowForUnit(MeasurementUnit.MILLIAMPER),
  [MeasurementUnit.MILLIVOLT]: flowForUnit(MeasurementUnit.MILLIVOLT),
  [MeasurementUnit.VOLT]: flowForUnit(MeasurementUnit.VOLT),
  [MeasurementUnit.BAR]: flowForUnit(MeasurementUnit.BAR),
  [MeasurementUnit.MILLIBAR]: flowForUnit(MeasurementUnit.MILLIBAR),
  [MeasurementUnit.METERS_OF_WATER_COLUMN]: flowForUnit(MeasurementUnit.METERS_OF_WATER_COLUMN),
  [MeasurementUnit.GALLON]: flowForUnit(MeasurementUnit.GALLON),
  [MeasurementUnit.LITER]: flowForUnit(MeasurementUnit.LITER),
  [MeasurementUnit.KW]: flowForUnit(MeasurementUnit.KW),
  [MeasurementUnit.KWH]: flowForUnit(MeasurementUnit.KWH),
  [MeasurementUnit.KVARH]: flowForUnit(MeasurementUnit.KVARH),
  [MeasurementUnit.DEG_C]: flowForUnit(MeasurementUnit.DEG_C),
  [MeasurementUnit.CUBIC_METER]: flowForUnit(MeasurementUnit.CUBIC_METER),
  [MeasurementUnit.KILOLITRE]: flowForUnit(MeasurementUnit.KILOLITRE),
  [MeasurementUnit.RELATIVE_HUMIDITY]: flowForUnit(MeasurementUnit.RELATIVE_HUMIDITY),
  [MeasurementUnit.VOLUMETRIC_WATER_CONTENT]: flowForUnit(MeasurementUnit.VOLUMETRIC_WATER_CONTENT),
  [MeasurementUnit.PERCENT]: flowForUnit(MeasurementUnit.PERCENT),
  [MeasurementUnit.METER]: flowForUnit(MeasurementUnit.METER),
  [MeasurementUnit.CENTIMETER]: flowForUnit(MeasurementUnit.CENTIMETER),
  [MeasurementUnit.MILLIMETER]: flowForUnit(MeasurementUnit.MILLIMETER),
  [MeasurementUnit.INCH]: flowForUnit(MeasurementUnit.INCH),
  [MeasurementUnit.FOOT]: flowForUnit(MeasurementUnit.FOOT),
  [MeasurementUnit.NO_UNITS]: flowForUnit(MeasurementUnit.NO_UNITS),
  [MeasurementUnit.DEG_F]: flowForUnit(MeasurementUnit.DEG_F),
  [MeasurementUnit.GRAM]: flowForUnit(MeasurementUnit.GRAM),
  [MeasurementUnit.KILOGRAM]: flowForUnit(MeasurementUnit.KILOGRAM),
  [MeasurementUnit.TON]: flowForUnit(MeasurementUnit.TON),
  [MeasurementUnit.NEWTON]: flowForUnit(MeasurementUnit.NEWTON),
  [MeasurementUnit.KILONEWTON]: flowForUnit(MeasurementUnit.KILONEWTON),
  [MeasurementUnit.MILLILITER]: flowForUnit(MeasurementUnit.MILLILITER),
  [MeasurementUnit.LITERS_PER_SECOND_SQUARE_METER]: flowForUnit(
    MeasurementUnit.LITERS_PER_SECOND_SQUARE_METER
  ),
  [MeasurementUnit.LITERS_PER_SQUARE_METER]: flowForUnit(MeasurementUnit.LITERS_PER_SQUARE_METER),
  [MeasurementUnit.MM_PER_HOUR_PER_SQUARE_METER]: flowForUnit(
    MeasurementUnit.MM_PER_HOUR_PER_SQUARE_METER
  ),
  [MeasurementUnit.MM_PER_SQUARE_METER]: flowForUnit(MeasurementUnit.MM_PER_SQUARE_METER),
  [MeasurementUnit.METERS_PER_SECOND]: flowForUnit(MeasurementUnit.METERS_PER_SECOND),
  [MeasurementUnit.KNOT]: flowForUnit(MeasurementUnit.KNOT),
  [MeasurementUnit.KILOMETER_PER_HOUR]: flowForUnit(MeasurementUnit.KILOMETER_PER_HOUR),
  [MeasurementUnit.PASCAL]: flowForUnit(MeasurementUnit.PASCAL),
  [MeasurementUnit.KILOPASCAL]: flowForUnit(MeasurementUnit.KILOPASCAL),
  [MeasurementUnit.HECTOPASCAL]: flowForUnit(MeasurementUnit.HECTOPASCAL),
  [MeasurementUnit.MILESPERHOUR]: flowForUnit(MeasurementUnit.MILESPERHOUR),
  [MeasurementUnit.PICOFARAD]: flowForUnit(MeasurementUnit.PICOFARAD),
  [MeasurementUnit.COUNTS_PER_MINUTE]: flowForUnit(MeasurementUnit.COUNTS_PER_MINUTE),
  [MeasurementUnit.MILLI_ROENTGEN_PER_HOUR]: flowForUnit(MeasurementUnit.MILLI_ROENTGEN_PER_HOUR),
  [MeasurementUnit.AQI]: flowForUnit(MeasurementUnit.AQI),
  [MeasurementUnit.MICRO_GRAMS_PER_CUBIC_METER]: flowForUnit(
    MeasurementUnit.MICRO_GRAMS_PER_CUBIC_METER
  ),
  [MeasurementUnit.SIEVERT]: flowForUnit(MeasurementUnit.SIEVERT),
  [MeasurementUnit.DEGREE]: flowForUnit(MeasurementUnit.DEGREE),
  [MeasurementUnit.LITERS_PER_SECOND]: flowForUnit(MeasurementUnit.LITERS_PER_SECOND),
  [MeasurementUnit.CUBIC_METERS_PER_HOUR]: flowForUnit(MeasurementUnit.CUBIC_METERS_PER_HOUR),
  [MeasurementUnit.LITERS_PER_SQUARE_METER_PER_HOUR]: flowForUnit(
    MeasurementUnit.LITERS_PER_SQUARE_METER_PER_HOUR
  ),
  [MeasurementUnit.PARTS_PER_MILLION]: flowForUnit(MeasurementUnit.PARTS_PER_MILLION),
  [MeasurementUnit.PARTS_PER_BILLION]: flowForUnit(MeasurementUnit.PARTS_PER_BILLION),
  [MeasurementUnit.NEPHELOMETRIC_TURBIDITY_UNIT]: flowForUnit(
    MeasurementUnit.NEPHELOMETRIC_TURBIDITY_UNIT
  ),
  [MeasurementUnit.MILLIGRAMS_PER_LITER]: flowForUnit(MeasurementUnit.MILLIGRAMS_PER_LITER),
  [MeasurementUnit.MILLIGRAMS_PER_CUBIC_METER]: flowForUnit(
    MeasurementUnit.MILLIGRAMS_PER_CUBIC_METER
  ),
  [MeasurementUnit.MICROSIEMENS_PER_CENTIMETER]: flowForUnit(
    MeasurementUnit.MICROSIEMENS_PER_CENTIMETER
  ),
  [MeasurementUnit.POTENTIAL_OF_HYDROGEN]: flowForUnit(MeasurementUnit.POTENTIAL_OF_HYDROGEN),
  [MeasurementUnit.KILOVOLT_AMPER]: flowForUnit(MeasurementUnit.KILOVOLT_AMPER),
  [MeasurementUnit.KILOVOLT_AMPER_REACTIVE]: flowForUnit(MeasurementUnit.KILOVOLT_AMPER_REACTIVE),
};
